import React from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@mui/styles';
import { Box, Button, Card, CardContent, Link, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import classNames from 'classnames';
import colorVariables from '../../styles/variables';
import EditAccess from '../../hocs/edit-access';

import advertisersImage from '../../images/empty-advertisers.png';
import campaignsImage from '../../images/empty-campaigns.png';
import contentOriginsImage from '../../images/empty-content-origins.png';
import contentStreamsImage from '../../images/empty-content-streams.png';
import creativesImage from '../../images/empty-creatives.png';
import lineItemsImage from '../../images/empty-line-items.png';
import previewClipsImage from '../../images/empty-preview-clips.png';
import dashboardImage from '../../images/empty-dashboard.png';
import timeSeriesImage from '../../images/empty-timeSeries.png';
import donutImage from '../../images/empty-donut.png';
import mapImage from '../../images/empty-map.png';
import { keyButton, baseButton } from '../../styles/styles';

export const EMPTY_RESULT_TYPES = {
  advertisers: 'advertisers',
  campaigns: 'campaigns',
  contentOrigins: 'contentOrigins',
  contentStreams: 'contentStreams',
  creatives: 'creatives',
  lineItems: 'lineItems',
  previewClips: 'previewClips',
  dashboard: 'dashboard',
  macros: 'macros',
  tags: 'tags',
  taggedStreams: 'taggedStreams',
  timeSeries: 'timeSeries',
  donut: 'donut',
  map: 'map',
  barChart: 'barChart',
  supplyRoutes: 'supplyRoutes',
};
const images = {
  [EMPTY_RESULT_TYPES.advertisers]: advertisersImage,
  [EMPTY_RESULT_TYPES.campaigns]: campaignsImage,
  [EMPTY_RESULT_TYPES.contentOrigins]: contentOriginsImage,
  [EMPTY_RESULT_TYPES.contentStreams]: contentStreamsImage,
  [EMPTY_RESULT_TYPES.creatives]: creativesImage,
  [EMPTY_RESULT_TYPES.supplyRoutes]: creativesImage,
  [EMPTY_RESULT_TYPES.lineItems]: lineItemsImage,
  [EMPTY_RESULT_TYPES.previewClips]: previewClipsImage,
  [EMPTY_RESULT_TYPES.dashboard]: dashboardImage,
  [EMPTY_RESULT_TYPES.macros]: advertisersImage,
  [EMPTY_RESULT_TYPES.tags]: advertisersImage,
  [EMPTY_RESULT_TYPES.taggedStreams]: advertisersImage,
  [EMPTY_RESULT_TYPES.timeSeries]: timeSeriesImage,
  [EMPTY_RESULT_TYPES.donut]: donutImage,
  [EMPTY_RESULT_TYPES.map]: mapImage,
  [EMPTY_RESULT_TYPES.barChart]: timeSeriesImage,
};

const messageTypeNameMap = {
  [EMPTY_RESULT_TYPES.advertisers]: 'Advertiser',
  [EMPTY_RESULT_TYPES.campaigns]: 'Campaign',
  [EMPTY_RESULT_TYPES.contentOrigins]: 'Content Origin',
  [EMPTY_RESULT_TYPES.contentStreams]: 'Content Stream',
  [EMPTY_RESULT_TYPES.creatives]: 'Creative',
  [EMPTY_RESULT_TYPES.supplyRoutes]: 'Supply Route',
  [EMPTY_RESULT_TYPES.lineItems]: 'Line Item',
  [EMPTY_RESULT_TYPES.previewClips]: 'Preview Clip',
  [EMPTY_RESULT_TYPES.dashboard]: 'No Data Available.',
  [EMPTY_RESULT_TYPES.macros]: 'Macros',
  [EMPTY_RESULT_TYPES.tags]: 'Tags',
  [EMPTY_RESULT_TYPES.taggedStreams]: 'Stream association',
  [EMPTY_RESULT_TYPES.timeSeries]: 'Please Select Metrics.',
  [EMPTY_RESULT_TYPES.donut]: 'Please Select Dimensions and Metrics.',
  [EMPTY_RESULT_TYPES.map]: 'Please Select Geo Dimensions and Metrics.',
  [EMPTY_RESULT_TYPES.barChart]: 'Please Select Dimensions and Metrics.',
};

/**
 * @type {object}
 */
const styles = {
  initialEmptyStateContainer: {
    width: '100%',
    height: '100%',
    minHeight: '500px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '70px',
  },
  emptyVizContainer: {
    paddingTop: 0,
  },
  dashboard: {
    position: 'relative',
    padding: '30px 64px 0px 0px',
  },
  dashboardMessage: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: '0px',
    left: '0px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& .MuiCard-root': {
      height: '100%',
      width: '100%',
    },
  },
  dashboardMessageTitle: {
    textAlign: 'center',
    fontSize: '24px',
    padding: '10px',
    fontWeight: '700',
  },
  dashboardImage: {
    width: '100%',
    height: 'auto',
  },
  initialEmptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    maxWidth: '375px',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '30px',
    marginTop: '40px',
  },
  learnMoreButton: {
    color: colorVariables.purple900,
    textDecoration: 'none',
  },
  keyButton,
  baseButton,
};

const useStyles = makeStyles(styles);

const TlInitialEmptyResults = ({
  messageType,
  buttonFunction,
  buttonText,
  knowledgeBaseUrl,
  showEmptyState,
  children,
  customStyles,
}) => {
  const classes = useStyles();
  const imageSrc = images[messageType];

  const dashboard = (
    <Box className={classNames(classes.initialEmptyStateContainer, customStyles.initialEmptyStateContainer)}>
      <Box className={classes.dashboard}>
        <Box className={classes.dashboardImage}>
          <img className={classes.dashboardImage} src={imageSrc} alt="transmit" />
        </Box>
        <Box className={classes.dashboardMessage}>
          <Box>
            <Card>
              <CardContent>
                <Typography className={classes.dashboardMessageTitle} component="h3">
                  {messageTypeNameMap[messageType]}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const buttonMsg = buttonText || `New ${messageTypeNameMap[messageType]}`;

  const list = (
    <Box className={classes.initialEmptyStateContainer}>
      <Box className={classes.initialEmptyState}>
        <Box>
          <img className={classes.image} src={imageSrc} alt="transmit" />
        </Box>
        <Box className={classes.buttons}>
          <EditAccess>
            <Button
              disableRipple
              disableElevation
              onClick={buttonFunction}
              className={classNames(classes.baseButton, classes.keyButton)}
              variant="contained"
              startIcon={buttonText ? null : <Add />}
            >
              {buttonMsg}
            </Button>
          </EditAccess>
          {knowledgeBaseUrl && (
            <Link className={classes.learnMoreButton} href={knowledgeBaseUrl}>
              Learn More
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );

  const emptyResultsJsx =
    messageType === EMPTY_RESULT_TYPES.timeSeries ||
    messageType === EMPTY_RESULT_TYPES.donut ||
    messageType === EMPTY_RESULT_TYPES.map ||
    messageType === EMPTY_RESULT_TYPES.barChart ||
    messageType === EMPTY_RESULT_TYPES.dashboard
      ? dashboard
      : list;

  return showEmptyState && buttonFunction && messageType ? emptyResultsJsx : children;
};

TlInitialEmptyResults.defaultProps = {
  knowledgeBaseUrl: '',
  showEmptyState: false,
  messageType: '',
  buttonFunction: () => {},
  buttonText: null,
  children: null,
  customStyles: {},
};

TlInitialEmptyResults.propTypes = {
  showEmptyState: PropTypes.bool,
  messageType: PropTypes.string,
  buttonFunction: PropTypes.func,
  buttonText: PropTypes.string,
  knowledgeBaseUrl: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.func]),
  customStyles: PropTypes.shape({}),
};

export default TlInitialEmptyResults;
